import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Grid,
  Button,
  Collapse,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { FilterList, ExpandMore, ExpandLess, Refresh } from '@mui/icons-material';
import SearchField from './SearchField';
import FilterSelect from './FilterSelect';

interface FilterConfig {
  key: string;
  label: string;
  type: 'select' | 'text' | 'multiselect' | 'date';
  options?: Array<{ label: string; value: string | number }>;
  placeholder?: string;
}

interface DataTableFilterProps {
  filters: FilterConfig[];
  onFiltersChange: (filterValues: Record<string, any>) => void;
  onSearchChange?: (searchValue: string) => void;
  searchPlaceholder?: string;
  showSearchBar?: boolean;
  showAdvancedFilters?: boolean;
  onRefresh?: () => void;
  disabled?: boolean;
  initialFilterValues?: Record<string, any>;
}

const DataTableFilter: React.FC<DataTableFilterProps> = ({
  filters,
  onFiltersChange,
  onSearchChange,
  searchPlaceholder = 'Search...',
  showSearchBar = true,
  showAdvancedFilters = true,
  onRefresh,
  disabled = false,
  initialFilterValues,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, any>>(
    initialFilterValues || filters.reduce((acc, filter) => ({ 
      ...acc, 
      [filter.key]: filter.type === 'multiselect' ? [] : '' 
    }), {})
  );
  const [showFilters, setShowFilters] = useState(false);

  const selectFilters = useMemo(() => filters.filter((f) => f.type === 'select' || f.type === 'multiselect'), [filters]);
  const textFilters = useMemo(() => filters.filter((f) => f.type === 'text'), [filters]);
  const dateFilters = useMemo(() => filters.filter((f) => f.type === 'date'), [filters]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    onSearchChange?.(value);
  };

  const handleFilterChange = (key: string, value: any) => {
    const newFilterValues = { ...filterValues, [key]: value };
    setFilterValues(newFilterValues);
    onFiltersChange(newFilterValues);
  };

  const handleResetFilters = () => {
    const resetValues = filters.reduce((acc, filter) => ({
      ...acc,
      [filter.key]: filter.type === 'multiselect' ? [] : '',
    }), {});
    setFilterValues(resetValues);
    setSearchValue('');
    onFiltersChange(resetValues);
    onSearchChange?.('');
  };

  const activeFiltersCount = Object.values(filterValues).filter((v) => Array.isArray(v) ? v.length > 0 : (v && v !== '')).length + (searchValue ? 1 : 0);
  const hasAdvancedFilters = selectFilters.length > 0 || textFilters.length > 0 || dateFilters.length > 0;

  return (
    <Paper
      sx={{
        p: 3,
        backgroundColor: '#ffffff',
        borderRadius: 3,
        border: '1px solid rgba(16,24,40,0.04)',
        boxShadow: '0 6px 20px rgba(15,23,42,0.06)',
      }}
    >
      {/* Search Bar */}
      {showSearchBar && (
        <Box sx={{ mb: showAdvancedFilters ? 2.5 : 0 }}>
          <SearchField
            value={searchValue}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder}
            disabled={disabled}
          />
        </Box>
      )}

      {/* Advanced Filters Toggle */}
      {showAdvancedFilters && hasAdvancedFilters && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: showFilters ? 2.5 : 0,
            pb: showFilters ? 0 : 0,
          }}
        >
          <Button
            startIcon={<FilterList />}
            endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setShowFilters(!showFilters)}
            disabled={disabled}
            sx={{
              color: '#0f172a',
              bgcolor: 'transparent',
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '0.95rem',
              borderRadius: 2,
              px: 1.5,
              py: 0.6,
              '&:hover': {
                backgroundColor: 'rgba(15, 23, 42, 0.04)',
                color: '#0b3a78'
              },
            }}
          >
            Advanced Filters
            {activeFiltersCount > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 1.25,
                  backgroundColor: '#0b63d6',
                  color: 'white',
                  borderRadius: '50%',
                  minWidth: 20,
                  height: 20,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  px: 0.5,
                }}
              >
                {activeFiltersCount}
              </Box>
            )}
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeFiltersCount > 0 && (
              <Button
                size="small"
                onClick={handleResetFilters}
                disabled={disabled}
                sx={{
                  color: '#f44336',
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                  },
                }}
              >
                Clear All
              </Button>
            )}
            {onRefresh && (
              <IconButton
                onClick={onRefresh}
                disabled={disabled}
                size="small"
                title="Refresh"
                sx={{
                  color: '#1967d2',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 103, 210, 0.08)',
                  },
                }}
              >
                <Refresh sx={{ fontSize: '1.3rem' }} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {/* Filters Section */}
      <Collapse in={showFilters} timeout="auto" unmountOnExit>
        <Box sx={{ pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Grid container spacing={2}>
            {selectFilters.map((filter) => {
              const mdSize = filter.key === 'propertyTypes' ? 6 : 4;
              return (
                <Grid item xs={12} sm={6} md={mdSize} key={filter.key}>
                  <FilterSelect
                    label={filter.label}
                    value={filterValues[filter.key]}
                    onChange={(value) => handleFilterChange(filter.key, value)}
                    options={filter.options || []}
                    multiple={filter.type === 'multiselect'}
                    disabled={disabled}
                    clearable={true}
                    onClear={() => handleFilterChange(filter.key, filter.type === 'multiselect' ? [] : '')}
                  />
                </Grid>
              );
            })}

            {textFilters.map((filter) => {
              const mdSize = filter.key === 'minBudget' || filter.key === 'maxBudget' ? 3 : 4;
              return (
                <Grid item xs={12} sm={6} md={mdSize} key={filter.key}>
                  <SearchField
                    value={filterValues[filter.key]}
                    onChange={(value) => handleFilterChange(filter.key, value)}
                    placeholder={filter.placeholder || `Filter by ${filter.label}...`}
                    disabled={disabled}
                  />
                </Grid>
              );
            })}

            {dateFilters.length > 0 && (
              <>
                {dateFilters.map((filter) => (
                  <Grid item xs={12} sm={6} md={3} key={filter.key}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                      {filter.label}
                    </Typography>
                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      value={filterValues[filter.key] || ''}
                      onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                      disabled={disabled}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                      }}
                    />
                  </Grid>
                ))}
              </>
            )}
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default DataTableFilter;
